// Dispatch records shipment and delivery. Sales owns payment and release details.
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { mergeTransporterNames } from '@/shared/constants/transporters';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { useUser } from '@/shared/hooks/useAuth';
import { canMarkDispatch } from '@/shared/utils/dispatchAccess';
import {
  DISPATCH_API_BASE,
  type DispatchStage,
  type ProofOfDelivery,
} from '@/features/sales/constants/dispatch';

export interface DispatchPanelDocument {
  _id: string;
  documentNumber: string;
  status: string;
  /** The SalesOrderModel this challan was raised from, if any — the advance-
   *  payment gate reads its linked invoice through this. */
  salesOrderModelId?: string;
  /** The AccountsCustomer this challan bills — populated ({_id, name}) by the
   *  documents list, but handled as a bare id too. Needed as the payment's
   *  partyId when recording one from this panel. */
  customerId?: string | { _id: string; name?: string } | null;
  dispatchStage?: DispatchStage;
  approvalRequired?: boolean;
  approvedAt?: string | null;
  approvalRequestedAt?: string | null;
  creditCheckPassed?: boolean;
  creditCheckBypassReason?: string;
  proofOfDelivery?: ProofOfDelivery;
  dispatchDetails?: {
    driverName?: string;
    driverPhone?: string;
    deliveryMethod?: string;
    transporterName?: string;
    vehicleNo?: string;
    docketNo?: string;
    receiverName?: string;
    receiverVehicleNo?: string;
    gatePassNo?: string;
  };
}

type DeliveryMethod = 'in_house_driver' | 'third_party_courier' | 'customer_transport' | 'local_transport';

const DELIVERY_METHOD_OPTIONS: Array<{ value: DeliveryMethod; label: string }> = [
  { value: 'in_house_driver', label: 'RBM in-house driver' },
  { value: 'third_party_courier', label: '3rd-party transporter (~90% of loads)' },
  { value: 'customer_transport', label: "Customer's own vehicle" },
];

interface Driver {
  _id: string;
  userId: { _id: string; display_name?: string; username?: string } | string;
  name: string;
  status: string;
}

interface Props {
  document: DispatchPanelDocument | null;
  onClose: () => void;
  /** Called after any change that the parent list needs to re-fetch for. */
  onChanged: () => void;
}

export function DispatchPanel({ document, onClose, onChanged }: Props) {
  const mayDispatch = canMarkDispatch(useUser());
  const [busy, setBusy] = useState(false);
  const [pod, setPod] = useState<ProofOfDelivery | null>(null);
  const [podForm, setPodForm] = useState({ receiverName: '', deliveredBy: '', customerConfirmedAt: '', notes: '' });
  const [signature, setSignature] = useState<File | null>(null);
  const [photos, setPhotos] = useState<FileList | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [method, setMethod] = useState<DeliveryMethod | ''>('');
  const [showNewDriverForm, setShowNewDriverForm] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', phone: '', vehicleNumber: '' });
  const [creatingDriver, setCreatingDriver] = useState(false);
  const [thirdPartyForm, setThirdPartyForm] = useState({ transporterName: '', vehicleNo: '', driverName: '', driverPhone: '', docketNo: '' });
  const [customerForm, setCustomerForm] = useState({ receiverName: '', receiverVehicleNo: '', gatePassNo: '' });
  const id = document?._id;

  const { data: transportationMasters = [] } = useMasterItems('transportation', undefined, true);
  const transporterOptions = useMemo(
    () => mergeTransporterNames([
      ...transportationMasters.map((item) => item.name),
      document?.dispatchDetails?.transporterName,
    ]),
    [document?.dispatchDetails?.transporterName, transportationMasters],
  );

  useEffect(() => {
    // Needed while still a draft too — a driver must be assignable BEFORE
    // dispatch, not only after (see the "Dispatch" button's disabled check
    // below), otherwise a challan can leave with nobody assigned to it.
    if (!['draft', 'dispatched'].includes(document?.status || '')) return;
    api.get<{ drivers: Driver[] }>('/api/fleet/drivers')
      .then((res) => setDrivers((res.data?.drivers || []).filter((d) => d.status === 'available')))
      .catch(() => setDrivers([]));
  }, [document?.status]);

  // Reset per document, then pull the current gate result and any POD on file.
  useEffect(() => {
    if (!id) return;
    setSignature(null);
    setPhotos(null);
    setPod(document?.proofOfDelivery?.captured ? document.proofOfDelivery : null);
    setMethod((document?.dispatchDetails?.deliveryMethod as DeliveryMethod) || '');
    setShowNewDriverForm(false);
    setNewDriver({ name: '', phone: '', vehicleNumber: '' });
    setThirdPartyForm({
      transporterName: document?.dispatchDetails?.transporterName || '',
      vehicleNo: document?.dispatchDetails?.vehicleNo || '',
      driverName: document?.dispatchDetails?.driverName || '',
      driverPhone: document?.dispatchDetails?.driverPhone || '',
      docketNo: document?.dispatchDetails?.docketNo || '',
    });
    setCustomerForm({
      receiverName: document?.dispatchDetails?.receiverName || '',
      receiverVehicleNo: document?.dispatchDetails?.receiverVehicleNo || '',
      gatePassNo: document?.dispatchDetails?.gatePassNo || '',
    });

    let cancelled = false;
    if (['dispatched', 'delivered'].includes(document?.status || '')) {
      api.get<ProofOfDelivery>(`${DISPATCH_API_BASE}/${id}/pod`)
        .then((res) => { if (!cancelled && res.data?.captured) setPod(res.data); })
        .catch(() => { /* no POD on file yet is the normal case, not an error */ });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!document) return null;

  const hasDriver = Boolean(document.dispatchDetails?.driverName);
  const hasDeliveryMethod = Boolean(document.dispatchDetails?.deliveryMethod);
  const noDeliveryInfo = !hasDriver && !hasDeliveryMethod;
  // Returns the resolved response on success (still truthy for every existing
  // `if (saved)` caller) so dispatchNow below can inspect the body for a
  // negative-stock warning — `false` on failure, unchanged.
  const run = async <T,>(fn: () => Promise<T>, successMessage: string, failureMessage: string): Promise<T | false> => {
    try {
      setBusy(true);
      const res = await fn();
      toast.success(successMessage);
      onChanged();
      return res;
    } catch (error) {
      toast.error(getApiErrorMessage(error, failureMessage));
      return false as const;
    } finally {
      setBusy(false);
    }
  };

  const dispatchNow = () => run(
    () => api.post<{ negativeStockWarnings?: { description: string; message: string }[] }>(`/api/accounts/commercial-documents/delivery-challans/${document._id}/actions/dispatch`, {}),
    'Dispatched. Sales can now see the updated status.',
    'Dispatch failed.',
  ).then((saved) => {
    if (!saved) return;
    // Allowed, not blocked — the goods may already be on the truck — but a
    // shortage that went through must not vanish silently.
    for (const w of saved.data?.negativeStockWarnings || []) {
      toast(`${w.description ? `${w.description}: ` : ''}${w.message}`, { icon: '⚠️', duration: 10000 });
    }
    onClose();
  });

  const savePod = () => {
    if (!podForm.receiverName.trim()) {
      toast.error('Receiver name is required to record a proof of delivery.');
      return Promise.resolve();
    }
    const body = new FormData();
    body.append('receiverName', podForm.receiverName.trim());
    body.append('deliveredBy', podForm.deliveredBy.trim());
    body.append('notes', podForm.notes);
    if (podForm.customerConfirmedAt) body.append('customerConfirmedAt', podForm.customerConfirmedAt);
    if (signature) body.append('signature', signature);
    if (photos) Array.from(photos).forEach((file) => body.append('photos', file));
    return run(
      async () => {
        const res = await api.post<ProofOfDelivery>(`${DISPATCH_API_BASE}/${document._id}/pod`, body);
        setPod(res.data);
      },
      'Proof of delivery recorded.',
      'Could not record the proof of delivery.',
    );
  };

  const fileUrl = (key: string) => `${DISPATCH_API_BASE}/${document._id}/pod/file/${key}`;

  const assignDriver = async () => {
    if (!selectedDriverId) {
      toast.error('Select a driver first.');
      return;
    }
    const driverUserId = drivers.find((d) => d._id === selectedDriverId)?.userId;
    const userId = typeof driverUserId === 'string' ? driverUserId : driverUserId?._id;
    setAssigningDriver(true);
    try {
      await api.post(`${DISPATCH_API_BASE}/${document._id}/assign-driver`, { driverId: userId });
      toast.success('Driver assigned — this delivery is now on their queue in the Driver App.');
      setSelectedDriverId('');
      onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not assign a driver.'));
    } finally {
      setAssigningDriver(false);
    }
  };

  /** Registers a driver who isn't in the Fleet list yet, then assigns them to
   *  this challan in the same step — for the in-house minority who don't
   *  already have a Fleet profile. */
  const createAndAssignDriver = async () => {
    if (!newDriver.name.trim() || !newDriver.phone.trim() || !newDriver.vehicleNumber.trim()) {
      toast.error('Driver name, phone and vehicle number are all required.');
      return;
    }
    setCreatingDriver(true);
    try {
      const res = await api.post<{ ok: boolean; driver: { _id: string; userId: string } }>('/api/fleet/drivers', newDriver);
      const created = res.data?.driver;
      await api.post(`${DISPATCH_API_BASE}/${document._id}/assign-driver`, { driverId: created?.userId });
      toast.success('Driver added and assigned.');
      setShowNewDriverForm(false);
      setNewDriver({ name: '', phone: '', vehicleNumber: '' });
      onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not add this driver.'));
    } finally {
      setCreatingDriver(false);
    }
  };

  /** Third-party transporter and customer-pickup details live directly on the
   *  challan (no Driver App tracking involved), so they're saved with a plain
   *  document PUT rather than the in-house assign-driver route. */
  const saveDeliveryDetails = (nextMethod: DeliveryMethod, details: Record<string, string>) =>
    run(
      () => api.put(`/api/accounts/commercial-documents/delivery-challans/${document._id}`, {
        dispatchDetails: { ...document.dispatchDetails, deliveryMethod: nextMethod, ...details },
      }),
      'Delivery details saved.',
      'Could not save delivery details.',
    );

  return (
    <Modal open onClose={onClose} title={`Dispatch — ${document.documentNumber}`} size="lg">
      <div className="space-y-5 text-sm">

        <section className="rounded-md border p-4">
          <p className="font-semibold">{document.status === 'draft' ? 'Pending dispatch' : document.status === 'dispatched' ? 'Dispatched' : document.status === 'delivered' ? 'Delivered' : document.status}</p>
          <p className="mt-1 text-xs text-muted-foreground">Sales can see the dispatch status and delivery updates.</p>
          {hasDeliveryMethod && <p className="mt-3">{document.dispatchDetails?.transporterName || document.dispatchDetails?.driverName || document.dispatchDetails?.deliveryMethod?.replaceAll('_', ' ')}</p>}
        </section>

        {/* ── Delivery details ────────────────────────────────────────────── */}
        {noDeliveryInfo && ['draft', 'dispatched'].includes(document.status) && (
          <section className="rounded-md border p-4">
            <p className="mb-1 font-semibold">Delivery details</p>
            <p className="mb-3 text-xs text-muted-foreground">
              Most loads go by an outside transporter, not an RBM driver — pick whichever actually applies.
              {document.status === 'draft' && noDeliveryInfo && ' Required before this challan can be dispatched.'}
            </p>

            {document.status === 'draft' && (
              <div className="mb-3 grid gap-2 sm:grid-cols-3">
                {DELIVERY_METHOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setMethod(opt.value)}
                    className={`rounded-md border px-2.5 py-2 text-left text-xs font-medium transition ${
                      method === opt.value ? 'border-primary bg-primary/5 text-primary' : 'border-muted-foreground/20 text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* In-house driver — Driver App tracked */}
            {(document.status === 'dispatched' ? document.dispatchDetails?.deliveryMethod === 'in_house_driver' || hasDriver : method === 'in_house_driver') && (
              <div>
                {document.dispatchDetails?.driverName ? (
                  <p className="text-muted-foreground">
                    Assigned to <strong>{document.dispatchDetails.driverName}</strong>
                    {document.dispatchDetails.driverPhone ? ` (${document.dispatchDetails.driverPhone})` : ''} — tracked in the Driver App until they scan the delivery QR.
                  </p>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <select
                        className="flex h-9 flex-1 rounded-md border bg-background px-3 text-sm"
                        value={selectedDriverId}
                        onChange={(event) => setSelectedDriverId(event.target.value)}
                      >
                        <option value="">Select an available driver…</option>
                        {drivers.map((d) => (
                          <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                      </select>
                      <Button size="sm" disabled={assigningDriver || !selectedDriverId} onClick={assignDriver}>
                        Assign
                      </Button>
                    </div>
                    {drivers.length === 0 && <p className="mt-2 text-xs text-muted-foreground">No available drivers right now.</p>}
                    {!showNewDriverForm ? (
                      <button type="button" className="mt-2 text-xs text-primary underline" onClick={() => setShowNewDriverForm(true)}>
                        + Add a new driver
                      </button>
                    ) : (
                      <div className="mt-3 space-y-2 rounded-md bg-muted/40 p-3">
                        <div className="grid gap-2 sm:grid-cols-3">
                          <Input placeholder="Driver name" value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} />
                          <Input placeholder="Phone" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} />
                          <Input placeholder="Vehicle number" value={newDriver.vehicleNumber} onChange={(e) => setNewDriver({ ...newDriver, vehicleNumber: e.target.value })} />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setShowNewDriverForm(false)}>Cancel</Button>
                          <Button size="sm" disabled={creatingDriver} onClick={createAndAssignDriver}>Add &amp; assign</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Third-party transporter — no Driver App involvement */}
            {(document.status === 'dispatched' ? document.dispatchDetails?.deliveryMethod === 'third_party_courier' : method === 'third_party_courier') && (
              document.status === 'dispatched' ? (
                <p className="text-muted-foreground">
                  <strong>{document.dispatchDetails?.transporterName || 'Transporter'}</strong>
                  {document.dispatchDetails?.vehicleNo ? ` — ${document.dispatchDetails.vehicleNo}` : ''}
                  {document.dispatchDetails?.driverName ? `, driver ${document.dispatchDetails.driverName}` : ''}
                  {document.dispatchDetails?.driverPhone ? ` (${document.dispatchDetails.driverPhone})` : ''}.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <select
                      className="flex h-9 rounded-md border bg-background px-3 text-sm"
                      value={thirdPartyForm.transporterName}
                      onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, transporterName: e.target.value })}
                    >
                      <option value="">Select transporter…</option>
                      {transporterOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                    <Input placeholder="Vehicle number" value={thirdPartyForm.vehicleNo} onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, vehicleNo: e.target.value })} />
                    <Input placeholder="Driver name" value={thirdPartyForm.driverName} onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, driverName: e.target.value })} />
                    <Input placeholder="Driver phone" value={thirdPartyForm.driverPhone} onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, driverPhone: e.target.value })} />
                    <Input placeholder="Docket / LR number" value={thirdPartyForm.docketNo} onChange={(e) => setThirdPartyForm({ ...thirdPartyForm, docketNo: e.target.value })} />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" disabled={busy || !thirdPartyForm.transporterName.trim()} onClick={() => saveDeliveryDetails('third_party_courier', thirdPartyForm)}>
                      Save transporter details
                    </Button>
                  </div>
                </div>
              )
            )}

            {/* Customer's own pickup */}
            {(document.status === 'dispatched' ? document.dispatchDetails?.deliveryMethod === 'customer_transport' : method === 'customer_transport') && (
              document.status === 'dispatched' ? (
                <p className="text-muted-foreground">
                  Picked up by <strong>{document.dispatchDetails?.receiverName || 'customer'}</strong>
                  {document.dispatchDetails?.receiverVehicleNo ? ` — ${document.dispatchDetails.receiverVehicleNo}` : ''}
                  {document.dispatchDetails?.gatePassNo ? `, gate pass ${document.dispatchDetails.gatePassNo}` : ''}.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Input placeholder="Received by (name)" value={customerForm.receiverName} onChange={(e) => setCustomerForm({ ...customerForm, receiverName: e.target.value })} />
                    <Input placeholder="Their vehicle number" value={customerForm.receiverVehicleNo} onChange={(e) => setCustomerForm({ ...customerForm, receiverVehicleNo: e.target.value })} />
                    <Input placeholder="Gate pass number" value={customerForm.gatePassNo} onChange={(e) => setCustomerForm({ ...customerForm, gatePassNo: e.target.value })} />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" disabled={busy || !customerForm.receiverName.trim()} onClick={() => saveDeliveryDetails('customer_transport', customerForm)}>
                      Save pickup details
                    </Button>
                  </div>
                </div>
              )
            )}
          </section>
        )}

        {/* ── Proof of delivery ───────────────────────────────────────────── */}
        {['dispatched', 'delivered'].includes(document.status) && (
          <section className="rounded-md border p-4">
            <p className="mb-2 font-semibold">Proof of delivery</p>
            {pod?.captured ? (
              <div className="space-y-1">
                <p>Received by <strong>{pod.receiverName}</strong>{pod.deliveredBy ? `, delivered by ${pod.deliveredBy}` : ''}.</p>
                {pod.customerConfirmedAt && (
                  <p className="text-muted-foreground">
                    Customer confirmed {new Date(pod.customerConfirmedAt).toLocaleString('en-IN')}.
                  </p>
                )}
                {pod.notes && <p className="text-muted-foreground">{pod.notes}</p>}
                <div className="flex flex-wrap gap-2 pt-2">
                  {pod.signatureFile?.storageKey && (
                    <a className="text-primary underline" target="_blank" rel="noopener noreferrer"
                      href={fileUrl(pod.signatureFile.storageKey)}>View signature</a>
                  )}
                  {(pod.photoFiles || []).map((file, index) => (
                    <a key={file.storageKey} className="text-primary underline" target="_blank" rel="noopener noreferrer"
                      href={fileUrl(file.storageKey)}>Photo {index + 1}</a>
                  ))}
                </div>
                <p className="pt-2 text-xs text-muted-foreground">Re-capture below to replace this record.</p>
              </div>
            ) : (
              <p className="mb-3 text-muted-foreground">Nothing on file yet.</p>
            )}

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="space-y-1"><span className="text-xs font-medium">Receiver name</span>
                <Input value={podForm.receiverName} onChange={(e) => setPodForm({ ...podForm, receiverName: e.target.value })} />
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Delivered by</span>
                <Input value={podForm.deliveredBy} onChange={(e) => setPodForm({ ...podForm, deliveredBy: e.target.value })} />
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Customer confirmed at</span>
                <Input type="datetime-local" value={podForm.customerConfirmedAt}
                  onChange={(e) => setPodForm({ ...podForm, customerConfirmedAt: e.target.value })} />
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Notes</span>
                <Input value={podForm.notes} onChange={(e) => setPodForm({ ...podForm, notes: e.target.value })} />
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Signature (PNG/JPEG/WebP)</span>
                <input type="file" accept="image/png,image/jpeg,image/webp" className="block w-full text-xs"
                  onChange={(e) => setSignature(e.target.files?.[0] || null)} />
              </label>
              <label className="space-y-1"><span className="text-xs font-medium">Photos (up to 5)</span>
                <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="block w-full text-xs"
                  onChange={(e) => setPhotos(e.target.files)} />
              </label>
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="outline" disabled={busy} onClick={savePod}>Save proof of delivery</Button>
            </div>
          </section>
        )}

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {document.status === 'draft' && !mayDispatch && (
            <p className="self-center text-xs text-on-surface-variant">Only the Dispatch team can dispatch this challan.</p>
          )}
          {document.status === 'draft' && mayDispatch && (
            <>
              {noDeliveryInfo && (
                <p className="self-center text-xs text-destructive">Fill in delivery details above first.</p>
              )}
              <Button
                disabled={busy || noDeliveryInfo}
                onClick={dispatchNow}
              >
                {busy ? 'Dispatching…' : 'Dispatch'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
